import { arraysAreEqual } from "../../../../components-shared/utils/array-utils.js";
import { html } from 'lit-html';
import { repeat } from 'lit-html/directives/repeat';
import { IProductWithVariants, IVariant, IVariantType } from './variant-selector-base.js';
import { DropDownVariantSelectorBase } from "./dropdown-variant-selector-base.js";
import { ProductApi } from "../../../api/archive/product/product-api.js"
import { IVariantShortInfoResponse } from '../../../api/archive/product/interfaces/i-variant-short-info-response.js';

const $ = window.jQuery;

export class DropDownVariantSelectorV3 extends DropDownVariantSelectorBase {
    private _variantTypeToCurrentValueMap: Map<number, number>;
    private _isOneTypeVariant: boolean = false;
    private _productApi: ProductApi = new ProductApi();
    private _variantsShortInfo: IVariantShortInfoResponse;
    private _valuesToFill: Array<Array<number>> = new Array<Array<number>>();

    constructor(name: string, selectorContainerElement: HTMLElement, product: IProductWithVariants, variantTypeId: number, translations: Map<string, string>, showPricesInclVat: boolean, loadAdditionalInfo: boolean) {
        super(name, 1, selectorContainerElement, product, variantTypeId, translations, showPricesInclVat, loadAdditionalInfo);

        this._variantTypeToCurrentValueMap = new Map(this.product.variantTypes.map((variantType) => [variantType.id, 0]));
        this._isOneTypeVariant = this.product.variantTypes.length <= 1;

        $(document).on(this.VARIANT_CHANGED_EVENT, this._onVariantChanged.bind(this));
    }

    protected template = () => html`
        <div class="c-variant-selector__term-definition c-variant-selector__term-definition_drop-down">
            <label for="${this.variantSelectorData.id}" class="c-variant-selector__label js-select-variant-label control-label">${this.variantSelectorData.variants.length > 1 ? this.variantSelectorData.labels.select : '' } ${this.variantSelectorData.variantTypeName}</label>
        </div>
        <div class="c-variant-selector__term-description c-variant-selector__term-description_drop-down">
            <select id="${this.variantSelectorData.id}" class="c-variant-selector__drop-down form-control">
                <option value="0" class="c-variant-selector__drop-down-option js-default-value">${this.variantSelectorData.labels.select}...</option>
                ${repeat(this.variantSelectorData.variants, (variant:IVariant) => html`
                    <option value="${variant.id}" data-name="${variant.name}">
                        ${variant.name}
                        <div class="ts-variant-info"></div>
                    </option>`
                )}
            </select>
        </div>`;

    protected getDataBeforeRender() {
        return {
            id: new Date().valueOf() + '_vt' + this.variantType.id,
            variantTypeName: this.variantType.name.toLowerCase(),
            variants: this.sortVariants(this.selectorContainerElement, this.variantType.variants, this.product),
            labels: { select: this.translations.get('selectVariantPrefix'), changeSelection: this.translations.get('changeSelectionVariantPrefix') }
        };
    }

    protected processAfterRender() {
        super.processAfterRender();
        const self = this;

        if(this.loadAdditionalInfo){
            this._productApi.getVariantsShortInfo(this.product.productId, this.showPricesInclVat).then(response => {
                self._variantsShortInfo = response;
                if(self._isOneTypeVariant){
                    self._fillVariantsInfo();
                } else {
                    self._checkValuesToFill();
                }
            });
        } else {
            if(self._isOneTypeVariant){
                self._fillVariantsInfo();
            } else {
                self._checkValuesToFill();
            }
        }
    }

    private _onVariantChanged(evt: CustomEvent, variantType: IVariantType, newVariantValue: number, isSelectedOptionAvailable: boolean, productId: number){
        if(this.product.productId !== productId || this._isOneTypeVariant || !this._variantTypeToCurrentValueMap.has(variantType.id)){
            return;
        }

        this._variantTypeToCurrentValueMap.set(variantType.id, newVariantValue);
        const otherVariantTypeToValueMap = new Map([...this._variantTypeToCurrentValueMap.entries()].filter(x => x[0] !== this.variantType.id));
        const allOtherValuesSelected = [...otherVariantTypeToValueMap.values()].every(variantValue => variantValue > 0);

        if(allOtherValuesSelected){
            this._fillVariantsInfo([...otherVariantTypeToValueMap.values()]);
        }
    }

    private _fillVariantsInfo(otherSelectedValues: Array<number> = null): void {
        if(this.loadAdditionalInfo && !this._variantsShortInfo){
            this._valuesToFill.push(otherSelectedValues);
            return;
        }

        const select = this.selectorContainerElement.querySelector('select') as HTMLSelectElement;
        for (var option of select.options) {
            const selectedVariantValue = parseInt(option.value);
            if(!selectedVariantValue){
                continue;
            }

            const selectedVariantValues = [selectedVariantValue];
            if(otherSelectedValues !== null) {
                selectedVariantValues.push(...otherSelectedValues);
            }

            const variantShortInfo = this._variantsShortInfo?.variantShortInfoList.find(variantShortInfo => arraysAreEqual(variantShortInfo.variantValues, selectedVariantValues));
            if(variantShortInfo){
                const variantName = option.getAttribute('data-name');
                option.text = `${variantName} ${variantShortInfo.priceString} ${variantShortInfo.discountPercent}%${variantShortInfo.isOutlet ? ' outlet' : ''}`;
            }
        }
    }

    private _checkValuesToFill(){
        const self = this;
        this._valuesToFill.forEach((valuesToFill: Array<number>) => {
            self._fillVariantsInfo(valuesToFill);
        })
        this._valuesToFill = null;
    }
}
